import { FormEvent, useEffect, useState } from 'react';

const REFERRAL_STORAGE_KEY = 'hosty_referral_code';
import { useSearchParams } from 'react-router-dom';
import { api } from '../../api/client';
import type { License } from '../../api/types';
import { Alert, Badge } from '../../components/ui/Badge';
import { Button } from '../../components/ui/Button';
import { usePricing } from '../../hooks/usePricing';
import { Card } from '../../components/ui/Card';
import { Input } from '../../components/ui/Input';
import { RegisterCtaButton } from '../../components/layout/RegisterCta';
import { LicenseBusinessProfilePanel } from '../../components/panel/LicenseBusinessProfilePanel';
import { VerifactuLicensePanel } from '../../components/panel/VerifactuLicensePanel';
import { StripeConnectPanel } from '../../components/panel/StripeConnectPanel';
import { useAuth } from '../../context/AuthContext';
import {
  formatDate,
  formatDateTime,
  licenseStatusLabel,
  licenseStatusTone,
} from '../../utils/format';

export function LicensesPage() {
  const pricing = usePricing();
  const { isDemo } = useAuth();
  const [searchParams] = useSearchParams();
  const [licenses, setLicenses] = useState<License[]>([]);
  const [posName, setPosName] = useState('');
  const [referralCode, setReferralCode] = useState('');
  const [referralStatus, setReferralStatus] = useState<'idle' | 'valid' | 'invalid'>('idle');
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState('');
  const [error, setError] = useState('');

  async function loadLicenses() {
    const data = await api.get<License[]>('/api/license/my-licenses');
    setLicenses(data);
  }

  useEffect(() => {
    loadLicenses().catch((err: Error) => setError(err.message));

    const refFromUrl = searchParams.get('ref');
    const storedRef = localStorage.getItem(REFERRAL_STORAGE_KEY);
    const initialRef = (refFromUrl ?? storedRef ?? '').trim().toUpperCase();
    if (initialRef) {
      setReferralCode(initialRef);
      if (refFromUrl) localStorage.setItem(REFERRAL_STORAGE_KEY, initialRef);
    }

    if (searchParams.get('dev_success')) {
      setMessage('Licencia creada correctamente.');
      return;
    }

    const sessionId = searchParams.get('session_id');
    if (!sessionId) return;

    setMessage('Procesando el pago…');
    let attempts = 0;
    const poll = window.setInterval(async () => {
      attempts += 1;
      try {
        const data = await api.get<License[]>('/api/license/my-licenses');
        setLicenses(data);
        if (data.length > 0) {
          setMessage('Licencia activada correctamente.');
          window.clearInterval(poll);
        } else if (attempts >= 15) {
          setMessage(
            'El pago se ha registrado. Si la licencia no aparece en unos minutos, recarga la página o contacta con soporte.'
          );
          window.clearInterval(poll);
        }
      } catch {
        if (attempts >= 15) {
          window.clearInterval(poll);
        }
      }
    }, 2000);

    return () => window.clearInterval(poll);
  }, [searchParams]);

  useEffect(() => {
    const code = referralCode.trim();
    if (!code) {
      setReferralStatus('idle');
      return;
    }

    const timer = window.setTimeout(async () => {
      try {
        const result = await api.get<{ success: boolean }>(
          `/api/license/validate-referral/${encodeURIComponent(code)}`
        );
        setReferralStatus(result.success ? 'valid' : 'invalid');
        if (result.success) {
          localStorage.setItem(REFERRAL_STORAGE_KEY, code.toUpperCase());
        }
      } catch {
        setReferralStatus('invalid');
      }
    }, 400);

    return () => window.clearTimeout(timer);
  }, [referralCode]);

  async function handleCreate(event: FormEvent) {
    event.preventDefault();
    setLoading(true);
    setError('');
    setMessage('');
    try {
      const payload: { pos_name: string; referral_code?: string } = { pos_name: posName };
      const code = referralCode.trim();
      if (code) {
        if (referralStatus === 'invalid') {
          setError('El código de referido no es válido. Revísalo o déjalo vacío.');
          setLoading(false);
          return;
        }
        payload.referral_code = code;
      }

      const result = await api.post<{ url?: string; message?: string }>(
        '/api/license/create-checkout-session',
        payload
      );
      if (result.url) {
        if (result.url.includes('dev_success')) {
          setMessage('Licencia creada correctamente.');
          await loadLicenses();
        } else {
          window.location.assign(result.url);
          return;
        }
      } else {
        setMessage(result.message ?? 'Solicitud procesada.');
        await loadLicenses();
      }
      setPosName('');
    } catch (err) {
      setError(err instanceof Error ? err.message : 'No se pudo crear la licencia');
    } finally {
      setLoading(false);
    }
  }

  async function handleCancel(subscriptionId: string | null) {
    if (!subscriptionId) return;
    if (!confirm('¿Cancelar la suscripción al final del periodo actual?')) return;
    try {
      await api.post('/api/license/cancel-subscription', { subscriptionId });
      setMessage('Suscripción cancelada. Seguirás activo hasta el fin del periodo.');
      await loadLicenses();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'No se pudo cancelar');
    }
  }

  async function handleReactivate(licenseId: number) {
    try {
      const result = await api.post<{ url?: string; message?: string }>(
        '/api/license/reactivate-subscription',
        { licenseId }
      );
      if (result.url) {
        window.location.href = result.url;
        return;
      }
      setMessage(result.message ?? 'Suscripción reactivada.');
      await loadLicenses();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'No se pudo reactivar');
    }
  }

  return (
    <div>
      <div className="panel-page-header">
        <div>
          <h1 className="page-title">Licencias</h1>
          <p className="page-subtitle">Contrata y gestiona una licencia por cada TPV.</p>
        </div>
      </div>

      {message ? <Alert tone="success">{message}</Alert> : null}
      {error ? <Alert tone="danger">{error}</Alert> : null}

      {isDemo ? (
        <Alert tone="info">
          Licencias de ejemplo. En tu cuenta real podrás contratar y gestionar suscripciones con
          Stripe.
        </Alert>
      ) : (
        <Card title="Contratar nueva licencia" subtitle={`${pricing.formatted} por TPV principal · Todos los módulos incluidos · IVA incluido`}>
          <form onSubmit={handleCreate} className="license-create-form">
            <Input
              label="Nombre del local / TPV"
              placeholder="Bar La Plaza"
              required
              value={posName}
              onChange={(e) => setPosName(e.target.value)}
            />
            <Input
              label="Código de referido (opcional)"
              placeholder="REF-XXXXXXXX"
              value={referralCode}
              onChange={(e) => setReferralCode(e.target.value.toUpperCase())}
              error={referralStatus === 'invalid' ? 'Código no válido o caducado.' : undefined}
              hint={
                referralStatus === 'valid'
                  ? 'Código válido. Esta suscripción contará para el programa de referidos del agente.'
                  : referralStatus === 'idle' && referralCode.trim()
                    ? 'Comprobando código…'
                    : referralStatus === 'idle'
                      ? 'Si un agente Hosty te recomendó el TPV, introduce su código aquí.'
                      : undefined
              }
            />
            <div className="license-create-form__submit">
              <Button type="submit" loading={loading}>
                Contratar licencia
              </Button>
            </div>
          </form>
        </Card>
      )}

      {!isDemo ? null : (
        <div style={{ marginTop: '1rem' }}>
          <RegisterCtaButton>Contratar licencias reales</RegisterCtaButton>
        </div>
      )}

      <div className="license-grid" style={{ marginTop: '1.5rem' }}>
        {licenses.length === 0 ? (
          <Card>
            <p className="ui-empty">Aún no tienes licencias. Contrata la primera para empezar.</p>
          </Card>
        ) : (
          licenses.map((license) => (
            <Card key={license.id} title={license.pos_name || `Licencia #${license.id}`}>
              <div className="license-card">
                <div className="license-card__meta">
                  <Badge tone={licenseStatusTone(license.status)}>
                    {licenseStatusLabel(license.status)}
                  </Badge>
                  <span>Clave: {license.license_key}</span>
                </div>
                <div className="license-card__meta">
                  <span>Alta: {formatDate(license.activation_date ?? license.created_at)}</span>
                  <span>Caducidad: {formatDate(license.expiry_date)}</span>
                  {license.hardware_id ? (
                    <span>Dispositivo: {license.hardware_id.slice(0, 12)}…</span>
                  ) : (
                    <span>Sin dispositivo vinculado</span>
                  )}
                </div>
                {license.trial_ends_at ? (
                  <p className="muted">Prueba hasta {formatDateTime(license.trial_ends_at)}</p>
                ) : null}
                <div className="license-card__actions">
                  {!isDemo && license.status === 'activa' && license.stripe_subscription_id ? (
                    <Button
                      variant="secondary"
                      size="sm"
                      onClick={() => handleCancel(license.stripe_subscription_id)}
                    >
                      Cancelar suscripción
                    </Button>
                  ) : null}
                  {!isDemo && license.status === 'cancelada' ? (
                    <Button variant="secondary" size="sm" onClick={() => handleReactivate(license.id)}>
                      Reactivar
                    </Button>
                  ) : null}
                </div>
                <LicenseBusinessProfilePanel licenseId={license.id} isDemo={isDemo} />
                <StripeConnectPanel licenseId={license.id} isDemo={isDemo} />
                <VerifactuLicensePanel licenseId={license.id} isDemo={isDemo} />
              </div>
            </Card>
          ))
        )}
      </div>
    </div>
  );
}
